export const queryKeys = {
  auth: {
    /** Raiz de `auth`, para os defaults por prefixo do `queryClient`. */
    all: () => ['auth'] as const,
    me: () => ['auth', 'me'] as const,
    csrf: () => ['auth', 'csrf'] as const,
  },
  /** The bell (GET /notifications, spec 20261028-132500): the session person's own notices. */
  notifications: {
    all: () => ['notifications'] as const,
    mine: () => ['notifications', 'mine'] as const,
    /** The Operação's Trabalho (GET /work-queue): by hospital, or one hospital when one is active. */
    workQueue: (hospitalUuid: string | null) => ['notifications', 'work-queue', hospitalUuid] as const,
  },
  /** The signed-in patient's own invoices (GET /me/invoices, spec 20261105-090000). */
  invoices: {
    all: () => ['invoices'] as const,
    mine: (filters: unknown) => ['invoices', 'mine', filters] as const,
  },
  /** The signed-in person's own profile (GET /users/me, P20EE-208). */
  profile: {
    all: () => ['profile'] as const,
    me: () => ['profile', 'me'] as const,
  },
  hospitals: {
    all: () => ['hospitals'] as const,
    list: (filters: unknown) => ['hospitals', 'list', filters] as const,
    detail: (id: number) => ['hospitals', 'detail', id] as const,
    /** Lista de hospitais do usuário autenticado (register/user/hospitals/list). */
    userList: () => ['hospitals', 'user-list'] as const,
    /** Lista completa de hospitais (register/hospitals/list). */
    registerList: () => ['hospitals', 'register-list'] as const,
    /** Hospitais de procedência (/hospitals/procedencia). */
    procedencia: () => ['hospitals', 'procedencia'] as const,
    /** Grupo de hospitais por id (/hospitals/group/:grupoId). */
    group: (grupoId: string) => ['hospitals', 'group', grupoId] as const,
    /** Detalhe do grupo (/hospitals/group/:grupoId/show). */
    groupShow: (grupoId: string) => ['hospitals', 'group-show', grupoId] as const,
    /** Notificações por uuid de hospital (/hospitals/:uuid/notifications). */
    notifications: (uuid: string) => ['hospitals', 'notifications', uuid] as const,
    /** Hospitais do usuário para o seletor de hospital ativo (`useHospitalList`). */
    activeList: (userId: number | undefined) => ['hospital_list', { user_id: userId }] as const,
    /** Raiz de `activeList`, para invalidação. */
    activeListAll: () => ['hospital_list'] as const,
    /** Trilha de AUDITORIA do que foi feito NO ESCOPO do hospital (/audit/hospitals/:id). */
    audit: (hospitalId: number, params: unknown) =>
      ['hospitals', 'audit', hospitalId, params] as const,
    /** Unidades com o tipo (GET /business-units), para a tela Unidades (spec 20261103-120000). */
    businessUnits: () => ['hospitals', 'business-units'] as const,
  },
  users: {
    all: () => ['users'] as const,
    list: (filters: unknown) => ['users', 'list', filters] as const,
    /** Lista simples sem filtros (MasterManagement metrics). */
    simpleList: (params?: unknown) => ['users', 'simple-list', params] as const,
    detail: (id: number) => ['users', 'detail', id] as const,
    /** Quem vê o quê no ERP (spec 20261015-160924 AC-6). */
    erpPermissions: (id: number) => ['users', 'erp-permissions', id] as const,
    logs: (filters: unknown) => ['users', 'logs', filters] as const,
    /** Logs por userId individual (Profile). */
    userLogs: (userId: number) => ['users', 'user-logs', userId] as const,
    /** Trilha de AUDITORIA do que aconteceu COM o usuário (/audit/users/:id). */
    audit: (userId: number, params: unknown) =>
      ['users', 'audit', userId, params] as const,
    /** Lookup de médicos por CRM/UF no Tasy (ApproveDoctor). */
    doctorByCrm: (crm: string, uf: string) => ['users', 'doctor-by-crm', crm, uf] as const,
    /** Usuários por grupo de hospitais (GroupProfile). */
    byGroup: (groupId: string, params: unknown) => ['users', 'by-group', groupId, params] as const,
    /** Usuários por hospital (MembersGroup). */
    byHospital: (hospitalId: string, params: unknown) => ['users', 'by-hospital', hospitalId, params] as const,
    /** Lookup por email (ChangeActiveHospitalModal). */
    byEmail: (params: unknown) => ['users', 'by-email', params] as const,
    /**
     * The paged user list of `useUsersQuery` (`{ users, totalPages, totalItems }`). Its own segment, not `list`'s:
     * `useUsersByHospitalsUserQuery` keeps a different shape under `list`, and one key over two shapes breaks
     * the cache. `users.all()` reaches both (P20EE-184).
     */
    page: (filters: unknown) => ['users', 'page', filters] as const,
  },
  exams: {
    all: () => ['exams'] as const,
    list: (filters: unknown) => ['exams', 'list', filters] as const,
    /** GET /appointments — lista paginada (Dashboard "Agendamento"). */
    appointments: (uuid: string, type: number, params: unknown) =>
      ['exams', 'appointments', uuid, type, params] as const,
    /** GET /authorizations — lista paginada (Dashboard "Autorização"). */
    authorizations: (uuid: string, type: number, params: unknown) =>
      ['exams', 'authorizations', uuid, type, params] as const,
    /** GET /attendances/range — exames por período (GridAttendance). */
    attendanceRange: (uuid: string, startDate: string, endDate: string, params: unknown) =>
      ['exams', 'attendance-range', uuid, startDate, endDate, params] as const,
    /** GET /treatments/last — último atendimento de paciente (SearchAppointment). */
    lastAppointment: (params: unknown) =>
      ['exams', 'last-appointment', params] as const,
    /** GET /treatments/appointments — atendimentos de paciente (SelectAppointment). */
    patientAppointments: (params: unknown) =>
      ['exams', 'patient-appointments', params] as const,
    /** GET /treatments/:uuid/:nr/details — detalhe de atendimento (Exams/Details). */
    treatmentDetails: (uuid: string, numberTreatment: string, userName: string) =>
      ['exams', 'treatment-details', uuid, numberTreatment, userName] as const,
    /** GET /treatments/:uuid/:nr/exams — exames de um tratamento (Exams/Details). */
    treatmentExams: (uuid: string, numberTreatment: string, params: unknown) =>
      ['exams', 'treatment-exams', uuid, numberTreatment, params] as const,
  },
  additions: {
    all: () => ['additions'] as const,
    list: (filters: unknown) => ['additions', 'list', filters] as const,
    detail: (id: number) => ['additions', 'detail', id] as const,
    /** The page /acrescimo/:id (spec 20261028-131000 AC-5): a core acréscimo or a v2 session, by its uuid. */
    page: (id: string) => ['additions', 'page', id] as const,
    /** One document of that page, as a blob. */
    pageDocument: (key: string) => ['additions', 'page-document', key] as const,
    counters: () => ['additions', 'counters'] as const,
    /** GET /addition/count — contador da aba "Acréscimos". */
    count: (filters: unknown) => ['additions', 'count', filters] as const,
    /** Raiz do contador, para invalidação com `exact: false`. */
    countAll: () => ['additions', 'count'] as const,
    /**
     * GET /compulsory/counter — contador da aba "Retiradas Compulsórias".
     * Namespace próprio herdado do código legado: as invalidações de compulsória hoje disparam
     * `additions.countAll()`, que NÃO alcança esta chave. Unificar muda quando o badge atualiza,
     * então fica registrado como achado, não como efeito colateral desta centralização.
     */
    compulsoryCount: (filters: unknown) => ['compulsoryCount', 'count', filters] as const,
    /** GET /addition/reasons, normalised to `{ id, reason }` (the Gestão de Acréscimos tab). One cache (P20EE-184). */
    reasons: () => ['addition', 'reasons'] as const,
    /** Arquivos de um acréscimo. */
    files: (owner: unknown) => ['addition', 'files', owner] as const,
    filesAll: () => ['addition', 'files'] as const,
    availableExams: (params: unknown) => ['availableExams', params] as const,
    requestDetail: (additionId: unknown) => ['requestDetail', additionId] as const,
    requestDetailAll: () => ['requestDetail'] as const,
    /** Contadores das abas da Gestão de Acréscimos; o filtro vai serializado, como o auto-refresh o compara. */
    requestCounters: (filtersJson: string) => ['request-counters', filtersJson] as const,
    requestCountersAll: () => ['request-counters'] as const,
  },
  compulsory: {
    all: () => ['compulsory'] as const,
    byAppointment: (appointmentId: unknown) => ['compulsory', appointmentId] as const,
    list: (filters: unknown) => ['compulsory', 'list', filters] as const,
    filter: (filters: unknown) => ['compulsory', 'filter', filters] as const,
    /** A tela de Retiradas Compulsórias, como o auto-refresh a nomeia. */
    page: (filtersJson: string, page: number, newRequestId: unknown, userId: unknown) =>
      ['compulsory', filtersJson, page, newRequestId, userId] as const,
  },
  featureFlags: {
    /** GET /feature-flags/:key pelo cliente público (`useFeatureFlag`). */
    public: (key: string) => ['feature-flags', key] as const,
    /** GET /feature-flags/:key pelo cliente autenticado (`useAuthFeatureFlag`). */
    auth: (key: string) => ['auth-feature-flags', key] as const,
  },
  reports: {
    all: () => ['reports'] as const,
    /**
     * GET /reports. O escopo é por papel para operação e por hospital ativo para os demais —
     * a expressão estava copiada em cinco arquivos, e basta um deles divergir para a lista de
     * relatórios do menu e a da tela de usuário apontarem para caches diferentes.
     */
    list: (roleId: number | undefined, uuid: string | undefined) =>
      ['reports', roleId === 1 ? { role_id: 1 } : { uuid }] as const,
    /** GET /reports/:id — detalhe usado pelo modal de edição e pela tela de relatório. */
    detail: (reportId: unknown) => ['report_details', { report_id: reportId }] as const,
    /** Parâmetros de um relatório (tela de relatório e modal de edição). */
    params: (reportId: unknown) => ['report_params', { reportId }] as const,
    /** O relatório gerado com os parâmetros enviados. */
    generated: (submitedParams: unknown) => ['report_generated', { submitedParams }] as const,
    /** Opções de um campo de seleção, pela rota que as serve. */
    selectOptions: (route: unknown) => ['select', { route }] as const,
  },
  carlao: {
    /** GET /internal/carlao-v2/sessoes/:id — a sessão da tela de acréscimo por leitura (spec 061). */
    sessaoV2: (sessionId: string) => ['carlao-v2', 'sessao', sessionId] as const,
    /** GET /internal/carlao-v2/sessoes/:id/revisao — a revisão da sessão, na Gestão de Acréscimos (spec 061 F). */
    revisaoV2: (sessionId: string) => ['carlao-v2', 'revisao', sessionId] as const,
    /** GET /internal/carlao-v2/catalogo/exames — the core exam catalogue, by name, alias or code. */
    coreExamSearch: (q: string) => ['carlao-v2', 'catalogo-exames', q] as const,
  },
  carlaoWizard: {
    session: (sessionId: unknown) => ['wizard-session', sessionId] as const,
    patientSearch: (name: unknown, dateOfBirth: unknown, hospitalUuid: unknown) =>
      ['wizard-patient-search', name, dateOfBirth, hospitalUuid] as const,
    attendances: (sessionId: unknown, hospitalUuid: unknown, birthDate: unknown) =>
      ['wizard-attendances', sessionId, hospitalUuid, birthDate] as const,
    examCatalogSearch: (q: unknown) => ['exam-catalog-search', q] as const,
  },
  internalDoctors: {
    /** Dados do médico no banco do portal, por página do formulário. */
    mysqlInfo: (userId: unknown, page: unknown) => ['mysql-info', userId, page] as const,
    mysqlInfoAll: () => ['mysql-info'] as const,
    /** Dados do médico no Tasy, por página do formulário. */
    tasyInfo: (userId: unknown, page: unknown) => ['tasy-info', userId, page] as const,
    tasyInfoAll: () => ['tasy-info'] as const,
    formInputs: (page: unknown) => ['form-inputs', page] as const,
    documents: () => ['documents'] as const,
    documentStatus: () => ['documentStatus'] as const,
    /** Documentos de um médico na gestão, por status e nome. */
    documentsValuesOfDoctor: (doctorId: unknown, documentStatusId: unknown, documentName: unknown) =>
      ['documentsValues', doctorId, documentStatusId, documentName] as const,
    /** Documentos do próprio médico. */
    documentsValuesOfUser: (userId: unknown) => ['documentsValues', userId] as const,
  },
  groups: {
    all: () => ['groups'] as const,
    list: () => ['groups', 'list'] as const,
  },
  partners: {
    all: () => ['partners'] as const,
    list: () => ['partners', 'list'] as const,
  },
  occurrences: {
    all: () => ['occurrences'] as const,
    list: (filters: unknown) => ['occurrences', 'list', filters] as const,
    /** Opções do filtro de médico. */
    doctorOptions: (search: unknown) => ['doctor_list', { search }] as const,
    /** Opções do filtro de motivo. */
    reasonOptions: (userId: unknown) => ['reason_list', { user_id: userId }] as const,
  },
  observability: {
    /**
     * GET /audit/access — trilha de ACESSO a dado de paciente (spec 008, US2).
     * Namespace próprio, separado de `users.audit`/`hospitals.audit`: aquelas são
     * a trilha de ALTERAÇÃO (quem mudou o quê). Misturar as duas na mesma raiz
     * faria uma invalidação de cadastro descartar o cache da outra pergunta.
     */
    accessTrail: (filters: unknown) => ['observability', 'access-trail', filters] as const,
  },
  dashboard: {
    // Propositalmente usa namespace ['analytics'] para que o QueryClient
    // aplique staleTime: 2min via setQueryDefaults(['analytics'], ...)
    metrics: (filters: unknown) => ['analytics', 'dashboard', filters] as const,
    /** Raiz `analytics`, a que recebe o `staleTime` de 2 min. */
    all: () => ['analytics'] as const,
  },
} as const
